import { Forma } from 'forma-embedded-view-sdk/auto';
import type { Child, GraphBuilding, GraphBuildingLevel, Urn } from 'forma-elements';

/**
 * Represents a building function with its properties and calculated value.
 */
export interface BuildingFunction {
	functionId: string;
	functionName: string;
	functionColor: string;
	value: number; // Only numeric values are considered; Forma may also return strings like "UNABLE_TO_CALCULATE".
	areas?: ComputedUnit[];
}

/**
 * Predefined geometry categories in Forma.
 */
export enum GeometryCategory {
	BUILDING = 'building',
	SITE_LIMIT = 'site_limit',
	TREE_LINE = 'tree_line',
	TREE_AREA = 'tree_area',
}

// @note: Implement like this because Forma doesn't have it in exports.
type FormaElement = Awaited<ReturnType<typeof Forma.elements.getByPath>>['element'];

type Point = [number, number];

interface BuildingElementData {
	children: Child[] | undefined;
	internalData: BuildingInternalData;
}

interface BuildingInternalData {
	floors: BuildingFloor[];
	units: BuildingUnit[];
}

interface BuildingFloor {
	id: string;
	height: number;
	spaces: Record<string, FloorSpace>;
}

interface FloorSpace {
	id: string;
	polygon: string[];
	holes: any[];
}

interface BuildingUnit {
	id: string;
	program: string | null;
	function?: string;
	functionId?: string;
	spaces: UnitSpaceMapping[];
}

interface UnitSpaceMapping {
	floorId: string;
	spaceId: string;
}

interface ComputedFloorData {
	id: string;
	height: number;
	level: number;
	spaces: Record<string, ComputedSpace>;
	units: ComputedUnit[];
}

interface ComputedSpace {
	id: string;
	polygon: string[];
	area: number;
	polygonPoints: [number, number][];
	wallSurfaces: WallSurface[];
	height: number;
	wallAreaTotal: number;
}

interface WallSurface {
	id: string;
	pointA: Point;
	pointB: Point;
	length: number;
	height: number;
	area: number;
}

export interface ComputedUnit {
	id: string;
	spaceId: string;
	floorArea: number;
	height: number;
	level: number;
	areaType: string;
	functionId?: string;
	function?: string;
	wallAreaTotal: number;
}

interface BuildingGraphInput {
	raw: FormaElement;
	element: BuildingElementData | undefined;
	graphBuilding: GraphBuilding | undefined;
}

/**
 * Data structure representing a geometry element with its functions.
 */
export type GeometryDataItem = {
	path: string;
	functions: BuildingFunction[];
	element: FormaElement;
};

/**
 * Returns currently selected paths in the Forma scene.
 */
export async function getSelectedPaths(): Promise<string[]> {
	return await Forma.selection.getSelection();
}

/**
 * //TODO: Currently not in use and is left in case we need it. Delete if not used after refactoring.
 * Retrieves the geometry paths for all building elements in the current Forma scene.
 * @returns A promise that resolves to an array of string paths uniquely identifying each building element.
 *
 * Example: ['root/81f176b', 'root/cxf156b'...]
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function getBuildingPaths(): Promise<string[]> {
	return await Forma.geometry.getPathsByCategory({ category: 'building' });
}

/**
 * Parses a geometry path into its floor number and building path components.
 *
 * A path ending in `/{number}` is treated as a floor path; the number is extracted
 * as the floor index and everything before it as the building path.
 * If no trailing number is found, the input is treated as a building path and
 * `floor` is returned as `undefined`.
 *
 * @param p - The geometry path to parse.
 * @returns An object containing:
 *   - `floor`: The floor number when a trailing number exists, otherwise `undefined`.
 *   - `building`: The building path portion (everything before the trailing number, or the full input).
 */
export function parsePath(p: string) {
	const m = p.match(/\/(\d+)$/);

	return {
		path: p,
		floor: m ? Number(m[1]) : undefined,
		building: m ? p.slice(0, m.index) : p,
	};
}

/**
 * Retrieves the Forma element associated with the given URN.
 *
 * @param urn – The unique resource identifier of the element to fetch.
 * @returns A promise that resolves to the requested element.
 */
export async function getElementByUrn(urn: Urn) {
	const el = await Forma.elements.get({ urn });
	return el.element;
}

/**
 * Retrieves the footprint of the site limit geometry, if any.
 *
 * Returns `null` when no geometry is tagged with the category `site_limit`.
 * When at least one such geometry exists, the footprint of the first one
 * is returned (the function assumes a single site-limit element).
 *
 * @returns A promise that resolves to the footprint polygon, or `null`.
 */
async function getSiteLimitFootprint() {
	const siteLimitPaths = await Forma.geometry.getPathsByCategory({ category: 'site_limit' });
	if (!siteLimitPaths.length) return null;

	return await Forma.geometry.getFootprint({ path: siteLimitPaths[0] });
}

/**
 * Retrieves all paths (polylines) that lie within the site limit boundary.
 *
 * The site limit is fetched via `getSiteLimitFootprint()`.
 * If no footprint is available, an empty array is returned immediately.
 *
 * @returns A promise that resolves to an array of path strings (each path is a
 *          GeoJSON-like coordinate array) that are fully contained within the
 *          site limit polygon.
 */
async function getPathsInsideSiteLimit(): Promise<string[]> {
	const footprint = await getSiteLimitFootprint();
	if (!footprint?.coordinates) return [];

	return await Forma.geometry.getPathsInsidePolygons({
		polygons: [footprint.coordinates],
	});
}

/**
 * Get building functions for given paths.
 *
 * @param paths Array of geometry paths.
 * @param areas Optional array of ComputedUnit objects to associate with the retrieved building functions.
 * @returns Array of valid BuildingFunction objects (value > 0).
 */
async function getBuildingFunctions(
	paths: string[],
	areas?: ComputedUnit[]
): Promise<BuildingFunction[]> {
	const areaMetrics = await Forma.areaMetrics.calculate({ paths });

	const breakdown = areaMetrics?.builtInMetrics?.grossFloorArea?.functionBreakdown ?? [];

	return breakdown
		.filter((f): f is BuildingFunction => typeof f.value === 'number' && f.value > 0)
		.map((func) => ({
			...func,
			areas: areas?.filter((area) => area.functionId === func.functionId) || [],
		}));
}

/**
 * Fetches the full building data (element + graph representation) for a given path.
 *
 * @param path – Geometry path of the building or one of its floors.
 * @returns Object containing the custom element structure and the graph-building data.
 */
async function getBuildingDataWithGraph(path: string): Promise<BuildingGraphInput | null> {
	const { building } = parsePath(path);

	const elByPath = await Forma.elements.getByPath({ path: building });
	if (!elByPath.element.representations?.graphBuilding) {
		return null;
	}

	const graphBuilding = await Forma.elements.representations.graphBuilding({
		urn: elByPath.element.urn,
	});

	return {
		raw: elByPath.element, // return original element
		element: {
			children: elByPath.element.children,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			internalData: (elByPath.element.representations?.__INTERNAL__ as any)?.data,
		},
		graphBuilding: graphBuilding?.data,
	};
}

/**
 * Maps raw building data into a structured format with computed floor information.
 *
 * @param data - Raw building data containing element and graph building information.
 * @returns An array of `ComputedFloorData` objects, each representing a floor within the building.
 */
function mapUnitsData(data: BuildingGraphInput): ComputedFloorData[] {
	const floors = data.element?.internalData.floors;
	const units = data.element?.internalData.units;
	const pointsData = data.graphBuilding?.levels;

	if (!floors) return [];
	if (!units) return [];
	if (!pointsData) return [];

	return floors.map((floor, floorIndex) => {
		const levelPoints = pointsData[floorIndex];

		const spacesData: Record<string, ComputedSpace> = {};
		const unitsData: ComputedUnit[] = [];
		const processedUnits = new Set<string>();

		Object.values(floor.spaces).forEach((space) => {
			const polygonIds = space.polygon.map((item) => `${item}-${floor.id}`);

			const polygonPoints = levelPoints
				? polygonIds
						.map((pointId) => levelPoints.points[pointId])
						.filter((point): point is [number, number] => point !== undefined)
				: [];

			const area = calculatePolygonArea(polygonPoints);

			const wallSurfaces = calculateWallSurfaces(
				space.id,
				floor.id,
				levelPoints,
				floor.height
			);

			spacesData[space.id] = {
				id: space.id,
				polygon: polygonIds,
				area,
				polygonPoints,
				wallSurfaces,
				height: floor.height,
				wallAreaTotal: wallSurfaces.reduce((sum, wall) => sum + wall.area, 0),
			};

			const relatedUnits = units.filter((unit) =>
				unit.spaces.some((s) => s.floorId === floor.id && s.spaceId === space.id)
			);

			relatedUnits.forEach((unit) => {
				if (!processedUnits.has(unit.id)) {
					const unitSpaceIds = unit.spaces
						.filter((s) => s.floorId === floor.id)
						.map((s) => s.spaceId);

					// Sums all wall surfaces for the unit
					const wallAreaTotal = unitSpaceIds.reduce((sum, spaceId) => {
						return sum + (spacesData[spaceId]?.wallAreaTotal || 0);
					}, 0);

					unitsData.push({
						id: unit.id,
						spaceId: unitSpaceIds[0],
						floorArea: spacesData[unitSpaceIds[0]]?.area || 0,
						wallAreaTotal,
						height: floor.height,
						level: floorIndex,
						areaType: unit.program || 'Unassigned',
						functionId: unit.functionId,
						...(unit.function && { function: unit.function }),
					});

					processedUnits.add(unit.id);
				}
			});
		});

		return {
			id: floor.id,
			height: floor.height,
			level: floorIndex,
			spaces: spacesData,
			units: unitsData,
		};
	});
}

/**
 * Calculates wall surfaces for a given space within a floor.
 *
 * Iterates over the outer loop surfaces of the space, computes the wall surface
 * area, and aggregates the results into an array of `WallSurface` objects.
 *
 * @param spaceId - Identifier of the space.
 * @param floorId - Identifier of the floor containing the space.
 * @param levelPoints - Graph building level data providing spatial context.
 * @param floorHeight - Height of the floor.
 * @returns Array of `WallSurface` objects representing the walls of the space.
 */
function calculateWallSurfaces(
	spaceId: string,
	floorId: string,
	levelPoints: GraphBuildingLevel,
	floorHeight: number
): WallSurface[] {
	if (!levelPoints || !levelPoints.spaces) return [];

	const spaceWithId = `${spaceId}-${floorId}`;
	const space = levelPoints.spaces.find((s) => s.id === spaceWithId);

	if (!space || !space.outerLoop) return [];

	const wallSurfaces: WallSurface[] = [];

	space.outerLoop.forEach((loopItem) => {
		const surfaceId = loopItem.surfaceId;
		const surface = levelPoints.surfaces?.find((s) => s.id === surfaceId);

		if (surface && surface.pointA && surface.pointB) {
			const pointA = levelPoints.points[surface.pointA];
			const pointB = levelPoints.points[surface.pointB];

			if (pointA && pointB) {
				const distance = calculateDistance(pointA, pointB);
				const wallArea = Math.round(distance * floorHeight * 100) / 100;

				wallSurfaces.push({
					id: surfaceId,
					pointA,
					pointB,
					length: Math.round(distance * 100) / 100,
					height: floorHeight,
					area: wallArea,
				});
			}
		}
	});

	return wallSurfaces;
}

/**
 * Calculates the area of a polygon
 *
 * @param coordinates Array of [x, y] coordinate pairs defining the polygon.
 * @returns The polygon's area rounded to two decimal places.
 */
function calculatePolygonArea(coordinates: number[][]): number {
	if (coordinates.length < 3) return 0;

	let area = 0;
	for (let i = 0; i < coordinates.length - 1; i++) {
		const [x1, y1] = coordinates[i];
		const [x2, y2] = coordinates[i + 1];
		area += x1 * y2 - x2 * y1;
	}

	return Math.round((Math.abs(area) / 2) * 100) / 100;
}

/**
 * Calculates the distance between two points.
 *
 * @param pointA – First point as [x, y].
 * @param pointB – Second point as [x, y].
 * @returns The distance between the two points, rounded to two decimals.
 */
function calculateDistance(pointA: Point, pointB: Point): number {
	const dx = pointB[0] - pointA[0];
	const dy = pointB[1] - pointA[1];
	return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Retrieves an element for the path and the specified category.
 * @param path.
 * @param category Desired geometry category.
 * @returns Forma element or null if category does not match.
 */
async function getElementDataByPathAndCategory(path: string, category: GeometryCategory) {
	const { element } = await Forma.elements.getByPath({ path });
	return element.properties?.category === category ? element : null;
}

/**
 * Retrieves building functions for all buildings within the site limit.
 *
 * @returns Array of GeometryDataItem objects for buildings inside the site limit,
 *          or an empty array if no buildings are found or site limit is not defined.
 */
export async function getSelectedBuildingFunctions(): Promise<GeometryDataItem[]> {
	const selected = await getSelectedPaths();
	const getBuildingDataWithGraphData = await getBuildingDataWithGraph(selected[0]);

	if (!getBuildingDataWithGraphData) return [];

	const areasData = mapUnitsData(getBuildingDataWithGraphData);

	const { floor } = parsePath(selected[0]);

	let areas: ComputedUnit[];
	if (floor === undefined) {
		areas = areasData.flatMap((item) => item.units); // extract only areas
	} else {
		areas = areasData[floor].units;
	}

	const functions = await getBuildingFunctions(selected, areas);
	if (!functions) return [];

	return [
		{
			path: selected[0],
			functions,
			element: (await Forma.elements.getByPath({ path: selected[0] })).element,
		},
	];
}

/**
 * Retrieves building functions for the selected buildings.
 *
 * @returns Array of GeometryDataItem objects for the selected buildings,
 *          or an empty array if no buildings are found or selected.
 */
export async function getBuildingFunctionsWithinSiteLimit(): Promise<GeometryDataItem[]> {
	const pathsInsidePathLimit = await getPathsInsideSiteLimit();

	const results = await Promise.all(
		pathsInsidePathLimit.map(async (path) => {
			const element = await getElementDataByPathAndCategory(path, GeometryCategory.BUILDING);
			if (!element) return null;

			// get graphBuild data
			const getBuildingDataWithGraphData = await getBuildingDataWithGraph(path);
			if (!getBuildingDataWithGraphData) return null;

			const areasData = mapUnitsData(getBuildingDataWithGraphData);

			const { floor } = parsePath(path);

			let areas: ComputedUnit[];
			if (floor === undefined) {
				areas = areasData.flatMap((item) => item.units);
			} else {
				areas = areasData[floor].units;
			}

			return {
				path,
				functions: await getBuildingFunctions([path], areas),
				element,
			};
		})
	);

	return results.filter(Boolean) as GeometryDataItem[];
}
